import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { FetchAPI, FilterAPI, Panel, Stamp, StampIcon, Token } from '@philips-emr/tws-components';
import { Color, LayoutJustify, Type } from '@philips-emr/tws-constants';
import Controller, { OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { MessageService } from '@philips-emr/tws-core/lib/services';

import { DocumentsAliases as Aliases, FragmentNames } from '../codegen';
import { Route } from '../route';

import BaseController from './BaseController';

enum StatusDocument {
  DENIED = 'R',
  EXPIRED = 'V',
  PENDING = 'P',
  CLOSE_TO_EXPIRE = 'X',
  ACTIVE = 'A'
}

interface Document {
  ieStatus: StatusDocument;
  ieStatusType: StatusDocument;
  dsStatus: string;
  dsStatusType: string;
  nrSeqProviderDoc: number;
  fileName: string;
  nrSeqProvider: number;
  notes: string;
  dsDocument: string;
  dtEmissionParam: string;
  dtValidityParam: string;
  nrSeqDocumentType: number;
  sendDocument: string;
  dtValidity: string;
  dtRefusal: string;
  noDocs: string;
  dtExpiration: string;
  dtIssue: string;
  undeterminedValidity: string;
  guidance: string;
}

@Controller({ fragment: FragmentNames.DOCUMENTS })
class DocumentsController extends BaseController {

  @Inject(Aliases.FETCH_DOCUMENTS)
  public fetchDocument: FetchAPI;

  @OnSelectItem(Aliases.DECK_DOCUMENT_TYPES)
  onSelectItem(event: SelectItemEvent<Document>): void {
    const { nrSeqDocumentType } = event.item;
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.historyService.push(Route.DOCUMENTS_DETAILS, { nrSeqDocumentType, nrSeqProvider });
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_DOCUMENTS)
  onRenderStatus(event: RenderEvent): void {
    if (event.data) {
      const { data, render } = event;
      const { sendDocument } = data as Document;
      if (sendDocument == 'S') {
        const content = (): ReactElement =>
          <Panel uuid="panel-token" className="token-documents__max-width" justifyContent={LayoutJustify.CENTER}>
            <Token value={MessageService.translate('provider.awaiting-document')} type={Type.WARNING} />
          </Panel>;
        render(content);
      }
    }
  }

  @OnMount(Aliases.FILTER_DOCS)
  onFilterMount(event: MountEvent): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const api = event.target as FilterAPI;
    api.setData({ nrSeqProvider });
  }
}

function getStampDocumentByStatus(ieStatusType: StatusDocument, dsStatus: string): ReactElement {
  return (<Stamp
    color={geStatusColor(ieStatusType)}
    icon={getStatusIcon(ieStatusType)}
    label={dsStatus}
  />
  );
}

function geStatusColor(ieStatusType: StatusDocument): Color {
  const colors = {
    [StatusDocument.ACTIVE]: Color.SAFETY_GREEN,
    [StatusDocument.CLOSE_TO_EXPIRE]: Color.SAFETY_ORANGE,
    [StatusDocument.DENIED]: Color.SAFETY_RED,
    [StatusDocument.EXPIRED]: Color.SAFETY_NEUTRAL,
    [StatusDocument.PENDING]: Color.SAFETY_NEUTRAL,
  };

  return colors[ieStatusType];
}

function getStatusIcon(ieStatusType: StatusDocument): StampIcon {
  const icons = {
    [StatusDocument.ACTIVE]: StampIcon.CHECK,
    [StatusDocument.CLOSE_TO_EXPIRE]: StampIcon.WARNING,
    [StatusDocument.DENIED]: StampIcon.CANCELED,
    [StatusDocument.EXPIRED]: StampIcon.CANCELED_OUTLINE,
    [StatusDocument.PENDING]: StampIcon.CLOCK,

  };
  return icons[ieStatusType];
}

export { DocumentsController, Document, getStampDocumentByStatus };
